import { Datetime } from '@aigc/components';
import { usePagingQueryHelper } from '@aigc/hooks';
import { useOverlay } from '@aigc/overlay-hook';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import {
  App,
  Button,
  Form,
  Input,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  type TableColumnsType,
} from 'antd';
import { useCallback, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { object, string } from 'yup';
import { FormControl } from '../../../components/form-control';
import { Permission } from '../../common/permision';
import { useAuthority } from '../../me/hook';
import { deleteSkillMutationOptions, getSkillsQueryOptions } from '../api';
import { SkillScope, toSkillScopeName, type SkillDto } from '../model';
import { SkillEditModal } from './skill-edit-modal';

const query_schema = object({
  name: string().trim().nullable().default(null),
});

export function SkillTable() {
  const { modal, message } = App.useApp();
  const { me } = useRouteContext({ from: '/_creator-center' });
  const authority = useAuthority();
  const [open_overlay] = useOverlay<boolean>();
  const form = useForm({
    defaultValues: { name: null as string | null },
    resolver: yupResolver(query_schema),
  });
  const { pagination, queryParams, queryMany, changePageNumber } =
    usePagingQueryHelper<{ name: string | null }>({
      defaultPagination: { page: 1, size: 10 },
      defaultQueryParams: { name: null },
      onRefresh: () => refetch(),
    });

  const { data, isFetching, refetch } = useQuery({
    ...getSkillsQueryOptions({
      page: pagination.page ?? 1,
      size: pagination.size ?? 10,
      name: queryParams?.name || undefined,
    }),
    placeholderData: keepPreviousData,
  });
  const { mutateAsync: delete_skill } = useMutation(deleteSkillMutationOptions);

  const openEditor = useCallback(
    (skill?: SkillDto) => {
      open_overlay(<SkillEditModal skill={skill} />);
    },
    [open_overlay],
  );

  const handleDelete = useCallback(
    (skill: SkillDto) => {
      modal.confirm({
        title: '确认删除技能',
        content: `确定要删除技能“${skill.name}”吗？删除后不可恢复。`,
        okText: '删除',
        cancelText: '取消',
        okButtonProps: { variant: 'filled', color: 'danger' },
        onOk: async () => {
          await delete_skill(skill.id);
          message.success('技能已删除');
        },
      });
    },
    [delete_skill, message, modal],
  );

  const columns = useMemo(() => {
    const _columns: TableColumnsType<SkillDto> = [
      { title: '名称', width: 240, render: (_, row) => row.name },
      {
        title: '描述',
        render: (_, row) => (
          <Tooltip title={row.description}>
            <div className="line-clamp-2">{row.description}</div>
          </Tooltip>
        ),
      },
      {
        title: '可用范围',
        width: 100,
        render: (_, row) => toSkillScopeName(row.scope),
      },
    ];

    if (authority(Permission.查看部门列表)) {
      _columns.push({
        title: '部门',
        width: 200,
        render: (_, row) => {
          return (
            <Space>
              {row.department_names?.map((name) => (
                <Tag key={name}>{name}</Tag>
              ))}
            </Space>
          );
        },
      });
    }

    _columns.push(
      {
        title: '创建人',
        width: 100,
        render: (_, row) => row.creator?.name ?? '—',
      },
      {
        title: '创建时间',
        width: 180,
        render: (_, row) => <Datetime value={row.created_at} />,
      },
      {
        title: '更新人',
        width: 100,
        render: (_, row) => row.updater?.name ?? '—',
      },
      {
        title: '更新时间',
        width: 180,
        render: (_, row) => <Datetime value={row.updated_at} />,
      },
      {
        title: '操作',
        fixed: 'right',
        width: 140,
        render: (_, row) => {
          const actions = (
            <Space size={8}>
              <Button
                size="small"
                variant="filled"
                color="default"
                onClick={() => openEditor(row)}
              >
                编辑
              </Button>
              <Button
                size="small"
                variant="filled"
                color="danger"
                onClick={() => handleDelete(row)}
              >
                删除
              </Button>
            </Space>
          );

          if (me.is_admin) {
            return actions;
          }

          if (
            me.is_department_admin &&
            row.creator?.name &&
            me.department_users.includes(row.creator.name) &&
            row.scope !== SkillScope.Global
          ) {
            return actions;
          }

          if (row.created_by === me.id && row.scope === SkillScope.Self) {
            return actions;
          }

          return null;
        },
      },
    );

    return _columns;
  }, [handleDelete, openEditor, me, authority]);

  const handleQuery = form.handleSubmit((values) => queryMany(values));
  const handleReset = () => {
    form.reset({ name: null });
    queryMany({ name: null });
  };

  return (
    <div className="h-full overflow-auto bg-white p-4">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>
          技能管理
        </Typography.Title>
        <Form layout="inline" onFinish={handleQuery}>
          <FormControl
            name="name"
            label="技能名称"
            control={form.control}
            render={({ field }) => (
              <Input
                {...field}
                value={field.value ?? ''}
                placeholder="请输入技能名称"
              />
            )}
          />
          <Space>
            <Button htmlType="submit" variant="filled" color="primary">
              查询
            </Button>
            <Button onClick={handleReset}>重置</Button>
          </Space>
        </Form>
        <div className="flex-auto" />
        <Space>
          <Button
            icon={<ReloadOutlined />}
            aria-label="刷新"
            onClick={() => refetch()}
          />
          <Button
            variant="solid"
            color="primary"
            icon={<PlusOutlined />}
            onClick={() => openEditor()}
          >
            新增技能
          </Button>
        </Space>
      </div>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isFetching}
        pagination={{
          current: pagination.page,
          pageSize: pagination.size,
          total: data?.total ?? 0,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: changePageNumber,
        }}
        scroll={{ x: 1440 }}
      />
    </div>
  );
}
